import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useOrganization } from "@clerk/nextjs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@coval/ui"
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react"
import { ClipboardList, ShieldAlert, UserSearch } from "lucide-react"

import { useIsOrgAdmin } from "@/hooks/useIsOrgAdmin"
import { useQueryParams } from "@/hooks/useQueryParams"
import { calculatePresetDateRange } from "@/utils/date/dateRange"

import { useDashboardFilters } from "../../contexts/DashboardFiltersContext"
import { useDashboardBatchTransportQuery } from "../../routes/dashboardBatchTransportQuery"
import type { ChartDataPoint } from "../../types/chart"
import { DEFAULT_WIDGET_DATE_RANGE } from "../../types/dateRange"
import { DASHBOARD_WIDGET_SCROLLBAR_STYLE } from "../../utils/scrollbarStyles"
import {
  getDashboardBatchRows,
  isTransportDashboardQueryLoading,
} from "../../utils/transportDashboardQueries"
import { BaseWidget, type WidgetProps } from "../BaseWidget"

type ViewMode = "by-metric" | "by-unique-call"

const BY_METRIC_COLUMNS = [
  { key: "simulation_output_id", label: "Call ID" },
  { key: "project_name", label: "Project" },
  { key: "metric_name", label: "Metric" },
  { key: "last_updated_at", label: "Reviewed At" },
  { key: "ground_truth_label", label: "Label" },
] as const

const BY_UNIQUE_CALL_COLUMNS = [
  { key: "simulation_output_id", label: "Call ID" },
  { key: "project_name", label: "Project" },
  { key: "metrics_reviewed", label: "Metrics Reviewed" },
  { key: "last_updated_at", label: "Last Reviewed At" },
] as const

const ASSIGNEE_QUERY_STALE_TIME_MS = 5 * 60 * 1000
const ASSIGNEE_QUERY_GC_TIME_MS = 15 * 60 * 1000

const formatTimestamp = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const TRUNCATED_ID_LENGTH = 8

const truncateId = (id: string): string =>
  id.length > TRUNCATED_ID_LENGTH ? `${id.slice(0, TRUNCATED_ID_LENGTH)}…` : id

const CallHistorySkeleton: React.FC = () => (
  <div className="flex flex-col">
    {/* Header row — col widths mirror the table: Call ID | Project | Metric | Reviewed At | Label */}
    <div className="grid grid-cols-[72px_1fr_2fr_148px_56px] gap-4 px-3 py-2 border-b border-coval-border">
      <Skeleton className="h-3 w-10 rounded" />
      <Skeleton className="h-3 w-12 rounded" />
      <Skeleton className="h-3 w-14 rounded" />
      <Skeleton className="h-3 w-20 rounded" />
      <Skeleton className="h-3 w-8 rounded" />
    </div>
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="grid grid-cols-[72px_1fr_2fr_148px_56px] gap-4 px-3 py-3 border-b border-coval-border"
      >
        <Skeleton className="h-3 w-14 rounded" />
        <Skeleton
          className={`h-3 rounded ${i % 3 === 0 ? "w-4/5" : i % 3 === 1 ? "w-3/5" : "w-2/3"}`}
        />
        <Skeleton
          className={`h-3 rounded ${i % 3 === 0 ? "w-4/5" : i % 3 === 1 ? "w-3/4" : "w-2/3"}`}
        />
        <Skeleton className="h-3 w-full rounded" />
        <Skeleton className={`h-3 rounded ${i % 2 === 0 ? "w-8" : "w-10"}`} />
      </div>
    ))}
  </div>
)

const AssigneeCallHistoryContent: React.FC<{
  onExportDataReady?: WidgetProps["onExportDataReady"]
}> = ({ onExportDataReady }) => {
  const isAdmin = useIsOrgAdmin()
  const { organization } = useOrganization()
  const queryParams = useQueryParams()
  const { state } = useDashboardFilters()
  const {
    dateRange: globalDateRange,
    globalDateRangeOverride,
    isOverrideActive,
  } = state

  const effectiveDateRange = useMemo(() => {
    if (isOverrideActive && globalDateRangeOverride) {
      return globalDateRangeOverride
    }
    if (globalDateRange) {
      return globalDateRange
    }
    return calculatePresetDateRange(DEFAULT_WIDGET_DATE_RANGE.preset!)
  }, [globalDateRange, globalDateRangeOverride, isOverrideActive])

  const [selectedAssignee, setSelectedAssignee] = useState<string>("")
  const [viewMode, setViewMode] = useState<ViewMode>("by-metric")

  const handleAssigneeChange = useCallback((value: string) => {
    setSelectedAssignee(value)
  }, [])

  const handleViewModeChange = useCallback((value: string) => {
    setViewMode(value as ViewMode)
  }, [])

  const assigneeVolumeQueryEnabled = isAdmin && Boolean(effectiveDateRange)
  const assigneeVolumeQuery = useDashboardBatchTransportQuery({
    request: {
      queries: [
        {
          query_id: "assignee_call_volume",
          kind: "queryAssigneeCallVolume",
          start_date: effectiveDateRange?.start?.toISOString(),
          end_date: effectiveDateRange?.end?.toISOString(),
        },
      ],
    },
    options: {
      enabled: assigneeVolumeQueryEnabled,
      staleTime: ASSIGNEE_QUERY_STALE_TIME_MS,
      gcTime: ASSIGNEE_QUERY_GC_TIME_MS,
    },
  })
  const assigneeData = useMemo(
    () =>
      getDashboardBatchRows<{
        assignee: string
      }>(assigneeVolumeQuery.data),
    [assigneeVolumeQuery.data],
  )
  const isLoadingAssignees = isTransportDashboardQueryLoading(
    assigneeVolumeQuery,
    assigneeVolumeQueryEnabled,
  )

  const assigneeOptions = useMemo(() => {
    if (!assigneeData) {
      return []
    }
    return assigneeData.map((row) => row.assignee).sort()
  }, [assigneeData])

  useEffect(() => {
    if (!selectedAssignee && assigneeOptions.length > 0) {
      setSelectedAssignee(assigneeOptions[0])
    }
  }, [assigneeOptions, selectedAssignee])

  const assigneeHistoryQueryEnabled =
    isAdmin && Boolean(selectedAssignee) && Boolean(effectiveDateRange)
  const assigneeHistoryQuery = useDashboardBatchTransportQuery({
    request: {
      queries: [
        {
          query_id: "assignee_call_history",
          kind: "queryAssigneeCallHistory",
          assignee: selectedAssignee,
          start_date: effectiveDateRange?.start?.toISOString(),
          end_date: effectiveDateRange?.end?.toISOString(),
        },
      ],
    },
    options: {
      enabled: assigneeHistoryQueryEnabled,
      staleTime: ASSIGNEE_QUERY_STALE_TIME_MS,
      gcTime: ASSIGNEE_QUERY_GC_TIME_MS,
    },
  })
  const callHistory = useMemo(
    () =>
      getDashboardBatchRows<{
        annotation_id: string
        simulation_output_id: string
        project_name: string | null
        metric_name: string | null
        last_updated_at: string
        ground_truth_label: string | null
      }>(assigneeHistoryQuery.data),
    [assigneeHistoryQuery.data],
  )
  const isLoadingHistory = isTransportDashboardQueryLoading(
    assigneeHistoryQuery,
    assigneeHistoryQueryEnabled,
  )
  const historyError = assigneeHistoryQuery.error

  const uniqueCallRows = useMemo(() => {
    if (!callHistory) {
      return []
    }
    const map = new Map<
      string,
      {
        simulation_output_id: string
        project_name: string | null
        metricsReviewed: number
        lastUpdatedAt: string
      }
    >()
    for (const row of callHistory) {
      const existing = map.get(row.simulation_output_id)
      if (existing) {
        existing.metricsReviewed += 1
        if (row.last_updated_at > existing.lastUpdatedAt) {
          existing.lastUpdatedAt = row.last_updated_at
        }
      } else {
        map.set(row.simulation_output_id, {
          simulation_output_id: row.simulation_output_id,
          project_name: row.project_name,
          metricsReviewed: 1,
          lastUpdatedAt: row.last_updated_at,
        })
      }
    }
    return Array.from(map.values())
  }, [callHistory])

  const exportChartData: ChartDataPoint[] = useMemo(() => {
    if (!callHistory || callHistory.length === 0) {
      return []
    }
    return callHistory.map((row) => ({
      date: `${row.simulation_output_id} | ${row.project_name ?? "—"} | ${row.ground_truth_label ?? "—"}`,
      timestamp: new Date(row.last_updated_at).getTime(),
      bucketTime: row.last_updated_at,
      value: 1,
    }))
  }, [callHistory])

  const prevExportRef = React.useRef<string>("")
  React.useEffect(() => {
    if (!onExportDataReady) {
      return
    }

    const key = JSON.stringify({
      assignee: selectedAssignee,
      rows: exportChartData.length,
      start: effectiveDateRange?.start?.toISOString(),
      end: effectiveDateRange?.end?.toISOString(),
    })
    if (prevExportRef.current === key) {
      return
    }
    prevExportRef.current = key

    if (exportChartData.length > 0) {
      onExportDataReady({
        widgetType: "chart",
        chartData: exportChartData,
        splitKeys: [],
        displayNameLookups: null,
        dateRange: effectiveDateRange
          ? { start: effectiveDateRange.start, end: effectiveDateRange.end }
          : undefined,
      })
    } else {
      onExportDataReady(null)
    }
  }, [exportChartData, effectiveDateRange, onExportDataReady, selectedAssignee])

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full text-coval-text-muted">
        <div className="text-center">
          <ShieldAlert className="w-8 h-8 mx-auto mb-2" />
          <p>Admin access required</p>
        </div>
      </div>
    )
  }

  const hasHistory = callHistory.length > 0

  return (
    <div className="flex flex-col h-full gap-3 p-3 overflow-hidden">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0">
          <Select
            value={selectedAssignee}
            onValueChange={handleAssigneeChange}
            disabled={isLoadingAssignees}
          >
            <SelectTrigger aria-label="Select assignee" variant="secondary">
              <SelectValue
                placeholder={
                  isLoadingAssignees
                    ? "Loading assignees…"
                    : "Select an assignee"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {assigneeOptions.map((email) => (
                <SelectItem key={email} value={email}>
                  {email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {selectedAssignee && !isLoadingHistory && hasHistory && (
          <div className="min-w-0 shrink-0">
            <Tabs value={viewMode} onValueChange={handleViewModeChange}>
              <TabsList variant="pill" aria-label="View mode">
                <TabsTrigger value="by-metric" variant="pill">
                  By Metric
                </TabsTrigger>
                <TabsTrigger value="by-unique-call" variant="pill">
                  By Unique Call
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        )}
      </div>

      {!selectedAssignee && !isLoadingAssignees && (
        <div className="flex items-center justify-center flex-1 text-coval-text-muted">
          <div className="text-center">
            <UserSearch className="w-8 h-8 mx-auto mb-2" />
            <p>Select an assignee to view their call history</p>
          </div>
        </div>
      )}

      {selectedAssignee && isLoadingHistory && <CallHistorySkeleton />}

      {selectedAssignee &&
        !isLoadingHistory &&
        historyError &&
        callHistory.length === 0 && (
          <div className="flex items-center justify-center flex-1 text-coval-text-muted">
            <div className="text-center">
              <ClipboardList className="w-8 h-8 mx-auto mb-2" />
              <p>Error loading call history</p>
            </div>
          </div>
        )}

      {selectedAssignee &&
        !isLoadingHistory &&
        !historyError &&
        callHistory.length === 0 && (
          <div className="flex items-center justify-center flex-1 text-coval-text-muted">
            <div className="text-center">
              <ClipboardList className="w-8 h-8 mx-auto mb-2" />
              <p>No calls found for this assignee in the selected date range</p>
            </div>
          </div>
        )}

      {selectedAssignee && !isLoadingHistory && hasHistory && (
        <>
          <div
            className="flex-1 overflow-auto min-h-0"
            style={DASHBOARD_WIDGET_SCROLLBAR_STYLE}
          >
            {viewMode === "by-metric" ? (
              <Table
                aria-label="Assignee call history by metric"
                isCompact
                isHeaderSticky
                removeWrapper
                classNames={{
                  th: "text-sm bg-surface-1 rounded-none",
                  td: "text-sm border-t border-coval-border py-3",
                }}
              >
                <TableHeader>
                  {BY_METRIC_COLUMNS.map((col) => (
                    <TableColumn key={col.key}>{col.label}</TableColumn>
                  ))}
                </TableHeader>
                <TableBody>
                  {callHistory.map((row) => (
                    <TableRow
                      key={row.annotation_id}
                      aria-label={`Open simulation ${row.simulation_output_id} in new tab`}
                      className="cursor-pointer hover:bg-surface-1"
                      onClick={() => {
                        if (!organization?.slug) {
                          return
                        }
                        window.open(
                          queryParams.getHref(
                            `/simulations/${row.simulation_output_id}`,
                            organization.slug,
                          ),
                          "_blank",
                        )
                      }}
                    >
                      <TableCell>
                        <span title={row.simulation_output_id}>
                          {truncateId(row.simulation_output_id)}
                        </span>
                      </TableCell>
                      <TableCell>{row.project_name ?? "—"}</TableCell>
                      <TableCell>{row.metric_name ?? "—"}</TableCell>
                      <TableCell>
                        {formatTimestamp(row.last_updated_at)}
                      </TableCell>
                      <TableCell>{row.ground_truth_label ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Table
                aria-label="Assignee call history by unique call"
                isCompact
                isHeaderSticky
                removeWrapper
                classNames={{
                  th: "text-sm bg-surface-1 rounded-none",
                  td: "text-sm border-t border-coval-border py-3",
                }}
              >
                <TableHeader>
                  {BY_UNIQUE_CALL_COLUMNS.map((col) => (
                    <TableColumn key={col.key}>{col.label}</TableColumn>
                  ))}
                </TableHeader>
                <TableBody>
                  {uniqueCallRows.map((row) => (
                    <TableRow
                      key={row.simulation_output_id}
                      aria-label={`Open simulation ${row.simulation_output_id} in new tab`}
                      className="cursor-pointer hover:bg-surface-1"
                      onClick={() => {
                        if (!organization?.slug) {
                          return
                        }
                        window.open(
                          queryParams.getHref(
                            `/simulations/${row.simulation_output_id}`,
                            organization.slug,
                          ),
                          "_blank",
                        )
                      }}
                    >
                      <TableCell>
                        <span title={row.simulation_output_id}>
                          {truncateId(row.simulation_output_id)}
                        </span>
                      </TableCell>
                      <TableCell>{row.project_name ?? "—"}</TableCell>
                      <TableCell>{row.metricsReviewed}</TableCell>
                      <TableCell>
                        {formatTimestamp(row.lastUpdatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export class AssigneeCallHistoryWidget extends BaseWidget {
  type = "assignee_call_history"
  title = "Assignee Call History"
  description = "Drill-down view of individual calls handled by an assignee"
  icon = (<UserSearch className="w-5 h-5" />)
  disabled = false

  getMinimumDimensions() {
    return { minW: 12, minH: 8 }
  }

  renderWidget(props: WidgetProps): React.ReactNode {
    return (
      <div className="w-full h-full select-none">
        <AssigneeCallHistoryContent
          onExportDataReady={props.onExportDataReady}
        />
      </div>
    )
  }

  getDefaultConfig() {
    return {
      title: "Assignee Call History",
      description: "Drill-down view of individual calls handled by an assignee",
    }
  }
}
